import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Fade from '@mui/material/Fade'
import { Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { memo, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  useNavigatorOnlineState,
  useNetworkState,
} from '@/modules/network/hooks/useNetworkState'
import { NetworkStatus } from '@/system/rxdb/replication'

import messages from './messages.ts'

const alertSx = (theme: Theme) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.snackbar,
})

const progressIconMapping = {
  info: <CircularProgress color="inherit" size={20} />,
}

const errorSyncIconMapping = {
  error: <CircularProgress color="inherit" size={20} />,
}

export const OfflineErrorAlerts = memo(function InnerOfflineErrorAlerts() {
  const { observeStatusState, observeLastErrorState } = useNetworkState()
  const [status, setStatus] = useState<NetworkStatus | undefined>(undefined)
  const [showOnlineState, setShowOnlineState] = useState(false)
  const [syncError, setSyncError] = useState<Error>()
  const { formatMessage } = useIntl()
  const { online, wasOffline, resetWasOffline } = useNavigatorOnlineState()

  useEffect(() => {
    const subscription = observeLastErrorState()?.subscribe((state) => {
      setSyncError(state?.error ? state?.error : undefined)
    })
    return () => {
      subscription.unsubscribe()
    }
  }, [observeLastErrorState])

  useEffect(() => {
    let timer: NodeJS.Timeout
    const subscription = observeStatusState()?.subscribe((status) => {
      setStatus(status)
      if (wasOffline && status === NetworkStatus.IDLE) {
        setShowOnlineState(true)
        timer = setTimeout(async () => {
          setShowOnlineState(false)
          resetWasOffline()
        }, 3000)
      }
    })
    return () => {
      subscription?.unsubscribe()
      if (timer) {
        clearTimeout(timer)
        setShowOnlineState(false)
        resetWasOffline()
      }
    }
  }, [observeStatusState, resetWasOffline, wasOffline])

  const closeAlert = useCallback(async () => {
    setShowOnlineState(false)
    resetWasOffline()
  }, [resetWasOffline])

  return (
    <>
      <Fade in={!online} unmountOnExit>
        <Alert sx={alertSx} severity="error">
          <Typography variant="body1">
            {formatMessage(messages.offlineAlertMessage)}
          </Typography>
        </Alert>
      </Fade>
      <Fade
        in={
          online &&
          !showOnlineState &&
          wasOffline &&
          status === NetworkStatus.SYNCING
        }
        unmountOnExit
      >
        <Alert sx={alertSx} iconMapping={progressIconMapping} severity="info">
          <Typography variant="body1">
            {formatMessage(messages.syncingAlertMessage)}
          </Typography>
        </Alert>
      </Fade>
      <Fade
        in={
          !wasOffline &&
          online &&
          Boolean(syncError) &&
          // ignore permission errors, they are handled somewhere else
          !syncError?.message?.startsWith('Permission')
        }
        unmountOnExit
      >
        <Alert sx={alertSx} iconMapping={errorSyncIconMapping} severity="error">
          <Typography variant="body1">
            {formatMessage(messages.syncingErrorMessage)}
          </Typography>
        </Alert>
      </Fade>
      <Fade
        unmountOnExit
        in={online && showOnlineState && status === NetworkStatus.IDLE}
      >
        <Alert sx={alertSx} severity="success" onClose={closeAlert}>
          <Typography variant="body1">
            {formatMessage(messages.onlineAlertMessage)}
          </Typography>
        </Alert>
      </Fade>
    </>
  )
})
